import { useDispatch } from 'react-redux';
import { Col, Form, Row } from 'antd';

import { convertUTCToLocal, formatDate, getLocalDateTime } from '@/utils/date';
import DatePicker from '@/views/components/Inputs/DatePicker';
import FormInputNumber from '@/views/components/Inputs/FormInputNumber';
import { CountrySelect, CurrencySelect, StateSelect } from '@/views/screens/common';
import { ShipmentBranchSelect } from '@/views/screens/Shipment';

import { CLAIMS_SHIPPER_CONSIGNEE_FETCH_ERROR_TYPE } from '../../../../../../contants/ClaimsEnums';
import { USER_PREFERENCES, YES_NO_OPTIONS } from '../../../../../../contants/common';
import { setWarningModal } from '../../../../../../redux/common/CommonActions';
import useUserPreference from '../../../../../../redux/hooks/useUserPreference';
import FormInput from '../../../../../components/Inputs/FormInput';
import FormSelectBox from '../../../../../components/Inputs/FormSelectBox';
import FormTextArea from '../../../../../components/Inputs/FormTextArea';
import HiddenFormInput from '../../../../../components/Inputs/HiddenFormInput';
import PrimaryLabel from '../../../../../components/Labels/PrimaryLabel';
import TransportationProviderSelector from '../../../../../components/Selectors/AsynchSearch/TransportationProviderSelector';
import LossAndDamageClaimStatusSelector from '../../../../../components/Selectors/LossAndDamageClaimStatusSelector';
import PostalCodeInput from '../../../../../components/Selectors/PostalCodeInput';
import TMSFiledCard from '../../../../common/components/TMSFiledCard';
import { useGetLADClaimInitialValues, useGetSelectedCompanyId } from '../../../hooks';
import {
  PreClaimsReasonSelect,
  PreClaimsReasonTypeSelect,
  PreClaimsShipperConsigneeShortName,
  PreClaimsTypeSelect,
} from '../../../PreClaims';

const LADClaimDetailsForm = ({ editMode, form, preparedBy, checkInstantClaimsDuplicate }) => {
  const dispatch = useDispatch();
  const selectedCompanyId = useGetSelectedCompanyId();
  const { data: initialCreateData } = useGetLADClaimInitialValues(selectedCompanyId);
  const consigneeKey = Form.useWatch('consigneeKey', form);
  const consigneeCountry = Form.useWatch('consigneeCountry', form);
  const originKey = Form.useWatch('originKey', form);
  const shipperCountry = Form.useWatch('shipperCountry', form);
  const dateShipped = Form.useWatch('dateShipped', form);
  const invoiceDate = Form.useWatch('invoiceDate', form);
  const apDate = Form.useWatch('apDate', form);
  const claimCloseDate = Form.useWatch('claimCloseDate', form);
  const checkDateToCustomer = Form.useWatch('checkDateToCustomer', form);
  const dateFiled = Form.useWatch('dateFiled', form);
  const dateAck = Form.useWatch('dateAck', form);
  const carrierName = Form.useWatch('carrierName', form);
  const carrierKey = Form.useWatch('carrierKey', form);

  const handleConsigneeFetch = (type, data) => {
    let consigneeData = {};
    consigneeData.countryCode = initialCreateData?.consigneeCountry;
    if (type === CLAIMS_SHIPPER_CONSIGNEE_FETCH_ERROR_TYPE.SUCCESS) {
      consigneeData = data;
    } else if (type === CLAIMS_SHIPPER_CONSIGNEE_FETCH_ERROR_TYPE.EMPTY) {
      dispatch(setWarningModal({ description: 'No data found', open: true }));
    } else if (type === CLAIMS_SHIPPER_CONSIGNEE_FETCH_ERROR_TYPE.CLEAR) {
      // empty
    } else if (type === CLAIMS_SHIPPER_CONSIGNEE_FETCH_ERROR_TYPE.ERROR) {
      dispatch(
        setWarningModal({
          description: 'Error while fetching data',
          open: true,
        }),
      );
    }
    form.setFieldsValue({
      consigneeAddr1: consigneeData?.addr1,
      consigneeAddr2: consigneeData?.addr2,
      consigneeCity: consigneeData?.city,
      consigneeCountry: consigneeData?.countryCode,
      consigneeKey: consigneeData?.claimShipRecKey,
      consigneeName: consigneeData?.consigneeShipperName,
      consigneeState: consigneeData?.state,
      consigneeZip: consigneeData?.zip,
    });
  };

  const handleConsigneeZipEntry = (data) => {
    form.setFieldsValue({
      consigneeCity: data?.city,
      consigneeCountry: data?.countryCode,
      consigneeState: data?.state,
      consigneeZip: data?.zip,
    });
  };

  const handleShipperFetch = (type, data) => {
    let consigneeData = {};
    consigneeData.countryCode = initialCreateData?.shipperCountry;
    if (type === CLAIMS_SHIPPER_CONSIGNEE_FETCH_ERROR_TYPE.SUCCESS) {
      consigneeData = data;
    } else if (type === CLAIMS_SHIPPER_CONSIGNEE_FETCH_ERROR_TYPE.EMPTY) {
      dispatch(setWarningModal({ description: 'No data found', open: true }));
    } else if (type === CLAIMS_SHIPPER_CONSIGNEE_FETCH_ERROR_TYPE.CLEAR) {
      // empty
    } else if (type === CLAIMS_SHIPPER_CONSIGNEE_FETCH_ERROR_TYPE.ERROR) {
      dispatch(
        setWarningModal({
          description: 'Error while fetching data',
          open: true,
        }),
      );
    }
    form.setFieldsValue({
      originKey: consigneeData?.claimShipRecKey,
      shipperAddr1: consigneeData?.addr1,
      shipperAddr2: consigneeData?.addr2,
      shipperCity: consigneeData?.city,
      shipperCountry: consigneeData?.countryCode,
      shipperName: consigneeData?.consigneeShipperName,
      shipperState: consigneeData?.state,
      shipperZip: consigneeData?.zip,
    });
  };

  const handleShipperZipEntry = (data) => {
    form.setFieldsValue({
      shipperCity: data?.city,
      shipperCountry: data?.countryCode,
      shipperState: data?.state,
      shipperZip: data?.zip,
    });
  };

  const userDateFormat = useUserPreference(USER_PREFERENCES.DEFAULT_DATE_FORMAT) || 'MM/DD/YYYY';

  const onChangeClaimStatus = (data) => {
    if (data?.value === 'C') {
      form.setFieldsValue({ claimCloseDate: getLocalDateTime().toISOString() });
    } else {
      form.setFieldsValue({ claimCloseDate: undefined });
    }
  };

  return (
    <Row gutter={30}>
      <Col sm={6}>
        <CurrencySelect
          margin="mb-10"
          editMode={editMode}
          name="currencyCode"
          rules={[
            {
              message: 'Please select a Currency',
              required: true,
            },
          ]}
        />
        <TransportationProviderSelector
          editMode={editMode}
          margin="mb-10"
          form={form}
          showPopupFilter
          label="Transportation Provider"
          labelForTestId="transportation-provider-selector"
          name="carrierKey"
          inputValue={carrierKey}
          carrierName={carrierName}
          disableOpen
          rules={[
            {
              message: 'Please select a Transportation Provider',
              required: true,
            },
          ]}
        />
        <PreClaimsShipperConsigneeShortName
          margin="mb-10"
          label="Consignee Short Name"
          name="consigneeShortName"
          editMode={editMode}
          onFetch={handleConsigneeFetch}
          maxLength={9}
        />
        <FormInput
          margin="mb-10"
          label="Consignee"
          name="consigneeName"
          editMode={editMode && !consigneeKey}
          validateTrigger={['onChange', 'onBlur']}
          rules={[{ message: 'Please enter the Consignee Name', required: true }]}
          maxLength={30}
        />
        <FormTextArea
          margin="mb-10"
          label="Address"
          name="consigneeAddr1"
          editMode={editMode && !consigneeKey}
          rows={2}
          rules={[
            {
              message: 'Please enter the Address',
              required: true,
            },
          ]}
          maxLength={35}
        />
        <FormTextArea
          margin="mb-10"
          label="Address 1"
          name="consigneeAddr2"
          editMode={editMode && !consigneeKey}
          rows={2}
          maxLength={35}
        />
        <CountrySelect
          margin="mb-10"
          label="Country"
          name="consigneeCountry"
          editMode={editMode && !consigneeKey}
          rules={[
            {
              message: 'Please select a Country',
              required: true,
            },
          ]}
        />
        <PostalCodeInput
          margin="mb-10"
          label="Postal Code"
          name="consigneeZip"
          editMode={editMode && !consigneeKey}
          rules={[
            {
              message: 'Please enter the consignee Postal Code',
              required: true,
            },
          ]}
          countryCode={consigneeCountry}
          handleZipDetails={handleConsigneeZipEntry}
          maxLength={10}
        />
        <StateSelect
          margin="mb-10"
          label="State"
          name="consigneeState"
          editMode={false}
          rules={[
            {
              message: 'Please select a state',
              required: true,
            },
          ]}
          countryCode={consigneeCountry}
        />
        <FormInput
          margin="mb-10"
          label="City"
          name="consigneeCity"
          editMode={false}
          validateTrigger={['onChange', 'onBlur']}
          rules={[
            {
              message: 'Please enter city',
              required: true,
            },
          ]}
        />
        <PreClaimsTypeSelect
          editMode={editMode}
          name="claimTypeKey"
          margin="mb-10"
          dataTestId="pre-claim-type-selector"
          rules={[
            {
              message: 'Please select a claim Type',
              required: true,
            },
          ]}
        />
        <FormInput margin="mb-10" label="Vehicle" name="vehicleNo" editMode={editMode} maxLength={20} />
      </Col>
      <Col sm={6}>
        <ShipmentBranchSelect
          editMode={editMode}
          label="Branch"
          name="branchKey"
          margin="mb-10"
          rules={[
            {
              message: 'Please select a Branch',
              required: true,
            },
          ]}
          customerId={selectedCompanyId}
        />
        <PreClaimsShipperConsigneeShortName
          margin="mb-10"
          label="Shipper Short Name"
          placeholder="Enter shipper short name"
          name="shipperShortName"
          dataTestId="shipper-short-name"
          editMode={editMode}
          onFetch={handleShipperFetch}
          maxLength={9}
        />
        <FormInput
          margin="mb-10"
          label="Shipper"
          name="shipperName"
          editMode={editMode && !originKey}
          validateTrigger={['onChange', 'onBlur']}
          rules={[
            {
              message: 'Please enter the Shipper Name',
              required: true,
            },
          ]}
          maxLength={30}
        />
        <FormTextArea
          margin="mb-10"
          label="Address"
          name="shipperAddr1"
          editMode={editMode && !originKey}
          rows={2}
          rules={[
            {
              message: 'Please enter the Address',
              required: true,
            },
          ]}
          maxLength={35}
        />
        <FormTextArea
          margin="mb-10"
          label="Address 1"
          name="shipperAddr2"
          editMode={editMode && !originKey}
          rows={2}
          maxLength={35}
        />
        <CountrySelect
          margin="mb-10"
          label="Country"
          name="shipperCountry"
          editMode={editMode && !originKey}
          rules={[
            {
              message: 'Please select a Country',
              required: true,
            },
          ]}
        />
        <PostalCodeInput
          margin="mb-10"
          label="Postal Code"
          name="shipperZip"
          editMode={editMode && !originKey}
          rules={[
            {
              message: 'Please enter the shipper Postal Code',
              required: true,
            },
          ]}
          countryCode={shipperCountry}
          handleZipDetails={handleShipperZipEntry}
          maxLength={10}
        />
        <StateSelect
          margin="mb-10"
          label="State"
          name="shipperState"
          editMode={false}
          rules={[
            {
              message: 'Please select a state',
              required: true,
            },
          ]}
          countryCode={shipperCountry}
        />
        <FormInput
          margin="mb-10"
          label="City"
          name="shipperCity"
          editMode={false}
          validateTrigger={['onChange', 'onBlur']}
          rules={[
            {
              message: 'Please enter city',
              required: true,
            },
          ]}
        />
        <FormInput
          margin="mb-10"
          label="Invoice Number"
          name="invoiceNo"
          dataTestId="invoice-no"
          editMode={editMode}
          maxLength={25}
          validateTrigger={['onChange', 'onBlur']}
          rules={[
            {
              message: 'Please enter Invoice Number',
              required: true,
            },
          ]}
          onBlur={checkInstantClaimsDuplicate('invoiceNo')}
        />
        <FormInput
          margin="mb-10"
          label="Freight Bill / Pro Number"
          name="claimProNo"
          editMode={editMode}
          validateTrigger={['onChange', 'onBlur']}
          rules={[
            {
              message: 'Please enter Freight Bill / Pro Number',
              required: true,
            },
          ]}
          maxLength={25}
          onBlur={checkInstantClaimsDuplicate('claimProNo')}
        />
      </Col>
      <Col sm={6}>
        {editMode ? (
          <DatePicker
            margin="mb-10"
            label="Ship Date"
            name="dateShipped"
            editMode={editMode}
            rules={[
              {
                message: 'Please select Ship Date',
                required: true,
              },
            ]}
            disabledTargetDate={new Date().toISOString()}
            disableFuture
          />
        ) : (
          <TMSFiledCard
            containerClass="mb-10"
            title={
              <span className="fw-700 f-14">
                Ship Date<span className="text-iconDanger f-16">*</span>
              </span>
            }
          >
            <PrimaryLabel>{formatDate(convertUTCToLocal(dateShipped), userDateFormat)}</PrimaryLabel>
          </TMSFiledCard>
        )}
        {editMode ? (
          <DatePicker margin="mb-10" label="Invoice Date" name="invoiceDate" editMode={editMode} />
        ) : (
          <TMSFiledCard containerClass="mb-10" title="Invoice Date">
            <PrimaryLabel>
              {invoiceDate ? formatDate(convertUTCToLocal(invoiceDate), userDateFormat) : '-'}
            </PrimaryLabel>
          </TMSFiledCard>
        )}
        {editMode ? (
          <DatePicker margin="mb-10" label="Accounts Payable Date" name="apDate" editMode={editMode} />
        ) : (
          <TMSFiledCard containerClass="mb-10" title="Accounts Payable Date">
            <PrimaryLabel>{formatDate(convertUTCToLocal(apDate), userDateFormat)}</PrimaryLabel>
          </TMSFiledCard>
        )}
        <FormInput
          margin="mb-30"
          label="Transportation Provider Claim Number"
          name="claimAckNo"
          editMode={editMode}
          maxLength={15}
        />
        <TMSFiledCard containerClass="mb-10" title="Prepared By">
          {preparedBy}
        </TMSFiledCard>
        <PreClaimsReasonTypeSelect
          editMode={editMode}
          name="reasonType"
          margin="mb-10"
          rules={[
            {
              message: 'Please select a claim Reason Type',
              required: true,
            },
          ]}
        />
        {editMode ? (
          <DatePicker margin="mb-10" label="Closed" name="claimCloseDate" editMode={editMode} />
        ) : (
          <TMSFiledCard containerClass="mb-15 mt-15" title={<span className="f-12">Closed</span>}>
            <PrimaryLabel>{formatDate(convertUTCToLocal(claimCloseDate), userDateFormat)}</PrimaryLabel>
          </TMSFiledCard>
        )}
        <FormInput
          margin="mb-10"
          label="Check Number from Transportation Provider"
          name="checkNoFromCarrier"
          editMode={editMode}
          dataTestId="check-no-from-carrier"
          maxLength={20}
        />
        <FormInput
          margin="mb-10"
          label="Check Number to Customer"
          dataTestId="check-no-to-customer"
          name="checkNoToCustomer"
          editMode={editMode}
          maxLength={20}
        />
        {editMode ? (
          <DatePicker margin="m-0" label="Date Refunded to Customer" name="checkDateToCustomer" editMode={editMode} />
        ) : (
          <TMSFiledCard containerClass="mb-10" title={<span className="fw-100 f-14">Date Refunded to Customer</span>}>
            <PrimaryLabel>{formatDate(convertUTCToLocal(checkDateToCustomer), userDateFormat)}</PrimaryLabel>
          </TMSFiledCard>
        )}
      </Col>
      <Col sm={6}>
        <FormInput
          margin="mb-10"
          label="Accounts Payable"
          name="apNumber"
          editMode={editMode}
          maxLength={18}
          dataTestId="ap-number"
        />
        {editMode ? (
          <DatePicker margin="mb-10" label="Filed" name="dateFiled" editMode={editMode} />
        ) : (
          <TMSFiledCard containerClass="mb-10" title={<span className="fw-100 f-14">Filed</span>}>
            <PrimaryLabel>{formatDate(convertUTCToLocal(dateFiled), userDateFormat)}</PrimaryLabel>
          </TMSFiledCard>
        )}
        {editMode ? (
          <DatePicker margin="mb-10" label="Date Acknowledged" name="dateAck" editMode={editMode} />
        ) : (
          <TMSFiledCard containerClass="mb-10" title={<span className="fw-100 f-14">Date Acknowledged</span>}>
            <PrimaryLabel>{formatDate(convertUTCToLocal(dateAck), userDateFormat)}</PrimaryLabel>
          </TMSFiledCard>
        )}
        <LossAndDamageClaimStatusSelector
          margin="mb-10"
          label="Status"
          name="claimStatusCode"
          editMode={editMode}
          rules={[
            {
              message: 'Please select a claim Status',
              required: true,
            },
          ]}
          onChange={onChangeClaimStatus}
        />
        <PreClaimsReasonSelect
          editMode={editMode}
          name="reasonCode"
          margin="mb-10"
          rules={[
            {
              message: 'Please select a claim Reason',
              required: true,
            },
          ]}
        />
        <FormInputNumber
          inputType="number"
          margin="mb-10"
          label="Amount Paid"
          name="claimPaidAmount"
          editMode={editMode}
          precision={2}
        />
        <FormInput margin="mb-10" label="Extension" name="claimExtension" editMode={editMode} maxLength={10} />
        <FormInput margin="mb-10" label="Accounting Code" name="acctCode" editMode={editMode} maxLength={26} />
        <FormInput margin="mb-10" label="Seal Number" name="sealNumber" editMode={editMode} maxLength={50} />
        <FormSelectBox
          margin="mb-10"
          label="Seal Intact"
          placeholder="Select Seal Intact"
          name="sealIntact"
          editMode={editMode}
          maxLength={1}
          options={YES_NO_OPTIONS}
          showSearch={false}
          shouldUseNativeSelect
          labelClass="fw-100"
        />
      </Col>

      {!editMode && (
        <>
          <HiddenFormInput name="dateShipped" />
          {/* TODO: check. it is already in the form */}
          <HiddenFormInput name="invoiceDate" />
          <HiddenFormInput name="apDate" />
          <HiddenFormInput name="claimCloseDate" />
          <HiddenFormInput name="checkDateToCustomer" />
          <HiddenFormInput name="dateFiled" />
          <HiddenFormInput name="dateAck" />
        </>
      )}
      <HiddenFormInput name="carrierName" />
      <HiddenFormInput name="consigneeKey" />
      <HiddenFormInput name="originKey" />
    </Row>
  );
};

export default LADClaimDetailsForm;
